import { useCallback, useMemo, useState } from 'react';

/**
 * Small client-side slicing helpers shared by every persona dashboard's
 * `AnalyticsSlicerBar`. The backend analytics endpoints are org-scoped only
 * (no filter query params), so every Cycle/Process/Status-style slicer in
 * Feature 5.3 filters the already-loaded rows in the browser rather than
 * refetching. Deliberately dependency-free (no react-query / router coupling)
 * so it's trivial to reuse across all 5 dashboards.
 */

export type SlicerOption = { value: string; label: string };
/** Selected slicer values keyed by slicer `key`; `''` means "All" (no filter) for that slicer. */
export type SlicerValues = Record<string, string>;

function defaultLabel(raw: string): string {
  return raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Builds a sorted, deduped option list from one field across a row array — the option list an `AnalyticsSlicerBar` select renders. */
export function deriveSlicerOptions<T>(
  rows: T[],
  accessor: (row: T) => string | null | undefined,
  formatLabel: (raw: string) => string = defaultLabel
): SlicerOption[] {
  const seen = new Map<string, string>();
  rows.forEach((row) => {
    const raw = accessor(row);
    if (!raw) return;
    if (!seen.has(raw)) seen.set(raw, formatLabel(raw));
  });
  return Array.from(seen.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Local (non-persisted-to-URL) state for a fixed set of named slicers, plus a `Clear` action and an active-filter count for the bar's chip. */
export function useSlicerState(keys: string[]): {
  values: SlicerValues;
  setValue: (key: string, value: string) => void;
  clear: () => void;
  activeCount: number;
} {
  const keySignature = keys.join('|');
  const initial = useMemo(() => Object.fromEntries(keys.map((k) => [k, ''])), [keySignature]); // eslint-disable-line react-hooks/exhaustive-deps
  const [values, setValues] = useState<SlicerValues>(initial);

  const setValue = useCallback((key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clear = useCallback(() => {
    setValues(Object.fromEntries(keys.map((k) => [k, ''])));
  }, [keySignature]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeCount = Object.values(values).filter(Boolean).length;

  return { values, setValue, clear, activeCount };
}

/** Filters `rows` against every active (non-empty) slicer value using a per-slicer accessor map. No-op (returns `rows` as-is) when nothing is active. */
export function applySlicers<T>(
  rows: T[],
  values: SlicerValues,
  accessors: Record<string, (row: T) => string | null | undefined>
): T[] {
  const active = Object.entries(values).filter(([, v]) => v);
  if (active.length === 0) return rows;
  return rows.filter((row) =>
    active.every(([key, val]) => {
      const accessor = accessors[key];
      if (!accessor) return true;
      return (accessor(row) ?? '') === val;
    })
  );
}

/** Buckets an ISO date string to a `YYYY-MM` key — used to derive a "Testing Period"/Cycle-style slicer from a date column when the row has no explicit cycle field. */
export function monthBucket(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Formats a `monthBucket` key ("2026-03") as a short human label ("Mar 2026"). */
export function monthLabel(bucket: string): string {
  const [y, m] = bucket.split('-').map(Number);
  if (!y || !m) return bucket;
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}
